<template>
  <div class="sls-log-query">
    <!-- 查询条件区域 - 吸顶 -->
    <div class="query-header" :class="{ 'is-sticky': isSticky }">
      <el-card shadow="never">
        <div class="query-form">
          <!-- 第一行：项目、时间、条数 -->
          <div class="form-row">
            <div class="form-item" style="flex: 2; min-width: 300px;">
              <label class="form-label">日志项目</label>
              <el-select
                v-model="selectedProduct"
                filterable
                placeholder="请选择日志项目"
                @change="handleProductChange"
                style="width: 100%;"
              >
                <el-option
                  v-for="item in productList"
                  :key="item.name"
                  :label="item.name"
                  :value="item.name"
                >
                  <span>{{ item.name }}</span>
                  <span style="float: right; color: #8492a6; font-size: 13px">{{ item.platform }}</span>
                </el-option>
              </el-select>
            </div>
            <div class="form-item" style="flex: 2; min-width: 380px;">
              <label class="form-label">时间范围</label>
              <el-date-picker
                v-model="timeRange"
                type="datetimerange"
                range-separator="至"
                start-placeholder="开始时间"
                end-placeholder="结束时间"
                format="YYYY-MM-DD HH:mm:ss"
                value-format="YYYY-MM-DD HH:mm:ss"
                :shortcuts="timeShortcuts"
                @change="handleTimeChange"
                style="width: 100%;"
              />
            </div>
            <div class="form-item" style="flex: 0 0 120px;">
              <label class="form-label">每页条数</label>
              <el-input-number
                v-model="queryParams.lines"
                :min="10"
                :max="100"
                :step="10"
                controls-position="right"
                style="width: 100%;"
              />
            </div>
          </div>

          <!-- 第二行：查询条件、字段选择、操作按钮 -->
          <div class="form-row">
            <div class="form-item" style="flex: 2; min-width: 300px;">
              <label class="form-label">
                查询条件
                <el-tooltip placement="top" effect="light" :width="500">
                  <template #content>
                    <div class="query-help">
                      <div class="help-title">SLS查询语法示例：</div>
                      <div class="help-item">• 精确匹配：{{ messageField }}: "错误信息"</div>
                      <div class="help-item">• 右侧模糊：{{ messageField }}: 关键词*</div>
                      <div class="help-item">• AND条件：{{ messageField }}: "错误" AND level: "ERROR"</div>
                      <div class="help-item">• OR条件：{{ messageField }}: "错误" OR {{ messageField }}: "异常"</div>
                      <div class="help-item">• NOT条件：NOT {{ messageField }}: "DEBUG"</div>
                      <div class="help-item">• 留空查询全部日志</div>
                      <div class="help-title" style="margin-top: 12px;">SQL查询示例：</div>
                      <div class="help-item">• LIKE查询：* | SELECT * WHERE {{ messageField }} LIKE '%错误%'</div>
                      <div class="help-item">• 统计查询：* | SELECT COUNT(*) as cnt GROUP BY level</div>
                      <div class="help-item">• 时间过滤：* | SELECT * WHERE __time__ > 1234567890</div>
                    </div>
                  </template>
                  <el-icon class="help-icon"><QuestionFilled /></el-icon>
                </el-tooltip>
              </label>
              <el-input
                v-model="queryParams.query"
                placeholder="输入查询条件，留空查询全部"
                clearable
              />
            </div>
            <div class="form-item" style="flex: 1; min-width: 200px;">
              <label class="form-label">显示字段</label>
              <el-select
                v-model="selectedFields"
                multiple
                filterable
                placeholder="选择显示字段"
                @change="handleFieldsChange"
                :teleported="false"
                style="width: 100%;"
              >
                <el-option
                  v-for="field in availableFields"
                  :key="field"
                  :label="field"
                  :value="field"
                />
              </el-select>
            </div>
            <div class="form-item" style="flex: 0 0 auto; align-self: flex-end;">
              <div class="form-actions">
                <el-button type="primary" :loading="loading" @click="handleSearch" :icon="Search">
                  查询
                </el-button>
                <el-button type="success" :loading="loading" @click="handleRealtimeQuery" plain>
                  实时查询
                </el-button>
                <el-button @click="handleReset" :icon="RefreshLeft">重置</el-button>
                <el-button @click="handleClear" :icon="Delete">清空</el-button>
              </div>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 统计信息 -->
    <div class="stats-bar" v-if="logs.length > 0">
      <div class="stats-item">
        <span class="stats-label">总条数：</span>
        <span class="stats-value">{{ total.toLocaleString() }}</span>
      </div>
      <div class="stats-item">
        <span class="stats-label">已加载：</span>
        <span class="stats-value">{{ logs.length.toLocaleString() }}</span>
      </div>
      <div class="stats-item">
        <span class="stats-label">查询模式：</span>
        <span class="stats-value">{{ queryMode }}</span>
      </div>
    </div>

    <!-- 日志内容区域 -->
    <div class="log-content" ref="logContainer" @scroll="handleScroll">
      <div v-if="logs.length === 0 && !loading" class="empty-state">
        <el-empty description="暂无日志数据，请选择项目并查询" />
      </div>

      <div v-else class="log-list">
        <div
          v-for="(log, index) in logs"
          :key="index"
          class="log-line"
          :class="{ 'log-line-odd': index % 2 === 0 }"
          @click="selectLine(index)"
          :data-line="index + 1"
        >
          <span class="log-line-number">{{ index + 1 }}</span>
          <span class="log-time">{{ formatTime(log[timeField] || log.__time__ || log.time) }}</span>
          <span class="log-separator">|</span>
          <span class="log-message">{{ log[messageField] || log.message || '' }}</span>
          <span v-for="field in getDisplayFields(log)" :key="field" class="log-field">
            <span class="log-separator">|</span>
            <span class="field-label">{{ field }}:</span>
            <span class="field-content">{{ formatFieldValue(log[field]) }}</span>
          </span>
        </div>

        <!-- 加载更多提示 -->
        <div v-if="loadingMore" class="loading-more">
          <el-icon class="is-loading"><Loading /></el-icon>
          <span>加载中...</span>
        </div>

        <!-- 没有更多数据提示 -->
        <div v-if="!hasNext && logs.length > 0" class="no-more">
          <el-divider>已加载全部日志</el-divider>
        </div>
      </div>

      <!-- 首次加载 -->
      <div v-if="loading && logs.length === 0" class="loading-state">
        <el-icon class="is-loading" :size="40"><Loading /></el-icon>
        <div>正在查询日志...</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, RefreshLeft, Delete, QuestionFilled, Loading } from '@element-plus/icons-vue'
import http from '@/utils/http'

// 数据
const productList = ref([])
const selectedProduct = ref('')
const currentProduct = ref(null)
const timeRange = ref([])
const selectedFields = ref([])
const availableFields = ref([])
const logs = ref([])
const loading = ref(false)
const loadingMore = ref(false)
const total = ref(0)
const hasNext = ref(false)
const queryMode = ref('simple')
const isSticky = ref(false)
const logContainer = ref(null)

// 查询参数
const queryParams = ref({
  logstore: '',
  platform: '',
  region: '',
  project: '',
  from: '',
  to: '',
  lines: 100,
  offset: 0,
  query: '',
  fields: ''
})

// 计算属性
const messageField = computed(() => {
  return currentProduct.value?.fields?.message || 'message'
})

const timeField = computed(() => {
  return currentProduct.value?.fields?.['time:'] || 'time'
})

// 时间快捷选项
const timeShortcuts = [
  {
    text: '最近1小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 1)
      return [start, end]
    }
  },
  {
    text: '最近3小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 3)
      return [start, end]
    }
  },
  {
    text: '最近6小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 6)
      return [start, end]
    }
  },
  {
    text: '最近12小时',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setHours(start.getHours() - 12)
      return [start, end]
    }
  },
  {
    text: '最近1天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 1)
      return [start, end]
    }
  },
  {
    text: '最近3天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 3)
      return [start, end]
    }
  },
  {
    text: '最近7天',
    value: () => {
      const end = new Date()
      const start = new Date()
      start.setDate(start.getDate() - 7)
      return [start, end]
    }
  }
]

// 方法
const loadProductList = async () => {
  try {
    const data = await http.getSlsLogProductList()
    if (data && Array.isArray(data)) {
      productList.value = data
    }
  } catch (error) {
    console.error('加载日志项目列表失败:', error)
    ElMessage.error('加载日志项目列表失败')
  }
}

const handleProductChange = async () => {
  const product = productList.value.find(p => p.name === selectedProduct.value)
  if (!product) return

  currentProduct.value = product
  queryParams.value.logstore = product.logstore
  queryParams.value.platform = product.platform
  queryParams.value.region = product.region
  queryParams.value.project = product.project

  // 加载字段列表
  await loadFields()

  // 设置默认字段
  if (product.fields) {
    const defaultFields = [product.fields['time:'], product.fields.message].filter(Boolean)
    selectedFields.value = defaultFields
    queryParams.value.fields = defaultFields.join(',')
  }

  // 清空日志
  logs.value = []
  total.value = 0

  // 自动查询最近1小时
  if (timeRange.value && timeRange.value.length === 2) {
    await handleSearch()
  }
}

const loadFields = async () => {
  if (!currentProduct.value) return

  try {
    const data = await http.getSlsLogFields({
      logstore: currentProduct.value.logstore,
      platform: currentProduct.value.platform,
      region: currentProduct.value.region,
      project: currentProduct.value.project
    })

    if (data && data.fields) {
      availableFields.value = data.fields
    }
  } catch (error) {
    console.error('加载字段列表失败:', error)
  }
}

const handleTimeChange = () => {
  if (timeRange.value && timeRange.value.length === 2) {
    queryParams.value.from = timeRange.value[0]
    queryParams.value.to = timeRange.value[1]
  }
}

const handleFieldsChange = () => {
  queryParams.value.fields = selectedFields.value.join(',')
}

const handleSearch = async () => {
  if (!selectedProduct.value) {
    ElMessage.warning('请先选择日志项目')
    return
  }

  if (!timeRange.value || timeRange.value.length !== 2) {
    ElMessage.warning('请选择时间范围')
    return
  }

  // 重置分页
  queryParams.value.offset = 0
  logs.value = []
  total.value = 0
  hasNext.value = false

  await queryLogs()
}

const handleRealtimeQuery = async () => {
  if (!selectedProduct.value) {
    ElMessage.warning('请先选择日志项目')
    return
  }

  // 设置最近1分钟的时间范围
  const end = new Date()
  const start = new Date()
  start.setMinutes(start.getMinutes() - 1)
  
  timeRange.value = [
    start.toISOString().slice(0, 19).replace('T', ' '),
    end.toISOString().slice(0, 19).replace('T', ' ')
  ]
  handleTimeChange()

  // 执行查询
  await handleSearch()
}

const queryLogs = async () => {
  if (loading.value || loadingMore.value) return

  if (queryParams.value.offset === 0) {
    loading.value = true
  } else {
    loadingMore.value = true
  }

  try {
    const data = await http.querySlsLog(queryParams.value)

    if (data) {
      if (queryParams.value.offset === 0) {
        logs.value = data.data || []
      } else {
        logs.value.push(...(data.data || []))
      }

      total.value = data.total || 0
      hasNext.value = data.hasNext || false
      queryMode.value = data.queryMode || 'simple'

      // 更新offset
      queryParams.value.offset = logs.value.length
    }
  } catch (error) {
    console.error('查询日志失败:', error)
    ElMessage.error('查询日志失败')
  } finally {
    loading.value = false
    loadingMore.value = false
  }
}

const handleScroll = async () => {
  const container = logContainer.value
  if (!container) return

  // 检查是否滚动到底部
  const scrollTop = container.scrollTop
  const scrollHeight = container.scrollHeight
  const clientHeight = container.clientHeight

  // 距离底部100px时加载更多
  if (scrollHeight - scrollTop - clientHeight < 100) {
    if (hasNext.value && !loadingMore.value && !loading.value) {
      await queryLogs()
    }
  }

  // 检查是否需要吸顶
  isSticky.value = scrollTop > 0
}

const handleReset = () => {
  queryParams.value.query = ''
  timeRange.value = []
  queryParams.value.from = ''
  queryParams.value.to = ''
}

const handleClear = () => {
  logs.value = []
  total.value = 0
  hasNext.value = false
  queryParams.value.offset = 0
}

const getDisplayFields = (log) => {
  // 获取需要显示的额外字段（除了时间和消息字段）
  const excludeFields = [messageField.value, timeField.value, '__time__', 'message', 'time']
  const allFields = Object.keys(log)
  
  // 如果用户选择了字段，只显示选中的字段（排除时间和消息）
  if (selectedFields.value.length > 0) {
    return selectedFields.value.filter(field => 
      !excludeFields.includes(field) && allFields.includes(field)
    )
  }
  
  // 否则显示所有额外字段
  return allFields.filter(key => !excludeFields.includes(key))
}

const formatTime = (timeValue) => {
  if (!timeValue) return '-'
  
  // 如果已经是格式化的时间字符串，直接返回
  if (typeof timeValue === 'string' && timeValue.includes('-')) {
    return timeValue
  }
  
  // 处理时间戳
  let timestamp = parseInt(timeValue)
  if (isNaN(timestamp)) return timeValue
  
  // 10位时间戳转13位
  if (timestamp.toString().length === 10) {
    timestamp = timestamp * 1000
  }
  
  // 转换为日期
  const date = new Date(timestamp)
  if (isNaN(date.getTime())) return timeValue
  
  // 格式化为 YYYY-MM-DD HH:mm:ss
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  const seconds = String(date.getSeconds()).padStart(2, '0')
  
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
}

const formatFieldValue = (value) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

const selectLine = (index) => {
  // 可以添加行选中效果或复制功能
  console.log('选中行:', index + 1)
}

// 生命周期
onMounted(async () => {
  await loadProductList()

  // 设置默认时间范围（最近1小时）
  const end = new Date()
  const start = new Date()
  start.setHours(start.getHours() - 1)
  timeRange.value = [
    start.toISOString().slice(0, 19).replace('T', ' '),
    end.toISOString().slice(0, 19).replace('T', ' ')
  ]
  handleTimeChange()
})

onUnmounted(() => {
  // 清理
})
</script>

<style scoped>
.sls-log-query {
  height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: #f5f7fa;
}

.query-header {
  flex-shrink: 0;
  padding: 16px;
  background: #fff;
  transition: all 0.3s;
}

.query-header.is-sticky {
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.query-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.form-row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-label {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

.help-icon {
  cursor: help;
  color: #909399;
  font-size: 16px;
}

.help-icon:hover {
  color: #409eff;
}

.query-help {
  padding: 8px;
  max-width: 500px;
}

.help-title {
  font-weight: bold;
  margin-bottom: 8px;
  color: #303133;
}

.help-item {
  margin: 4px 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.form-actions {
  display: flex;
  gap: 8px;
}

.stats-bar {
  flex-shrink: 0;
  display: flex;
  gap: 24px;
  padding: 12px 16px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  font-size: 14px;
}

.stats-item {
  display: flex;
  align-items: center;
}

.stats-label {
  color: #909399;
  margin-right: 4px;
}

.stats-value {
  color: #409eff;
  font-weight: 600;
}

.log-content {
  flex: 1;
  overflow: auto;
  padding: 0;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  margin: 0 16px 16px 16px;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  width: 100%;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  min-height: 300px;
  gap: 16px;
  color: #909399;
  font-size: 14px;
}

.log-list {
  display: flex;
  flex-direction: column;
  font-family: 'Consolas', 'Monaco', 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  min-width: max-content;
  width: 100%;
}

.log-line {
  display: flex;
  align-items: flex-start;
  padding: 2px 12px;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.1s;
  white-space: nowrap;
  min-height: 24px;
  cursor: pointer;
}

.log-line:hover {
  background-color: #e8f4ff;
}

.log-line-odd {
  background-color: #fafafa;
}

.log-line-odd:hover {
  background-color: #e8f4ff;
}

.log-line-number {
  color: #999;
  margin-right: 12px;
  flex-shrink: 0;
  min-width: 50px;
  text-align: right;
  font-size: 12px;
  user-select: none;
  border-right: 1px solid #e0e0e0;
  padding-right: 12px;
}

.log-time {
  color: #666;
  margin-right: 8px;
  flex-shrink: 0;
  min-width: 160px;
  font-weight: 500;
}

.log-separator {
  color: #ddd;
  margin: 0 8px;
  flex-shrink: 0;
}

.log-message {
  color: #333;
  white-space: pre;
  flex-shrink: 0;
}

.log-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  margin-left: 8px;
}

.field-label {
  color: #666;
  font-size: 12px;
  font-weight: 500;
}

.field-content {
  color: #0066cc;
  font-size: 12px;
  white-space: pre;
}

.loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 20px;
  color: #909399;
  font-size: 14px;
}

.no-more {
  padding: 20px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}

/* 滚动条样式 */
.log-content::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.log-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.log-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 响应式 */
@media (max-width: 1200px) {
  .form-row {
    flex-direction: column;
  }

  .form-item {
    width: 100%;
  }
}
</style>
